import { useId } from "react";

import { observer } from "mobx-react-lite";
import { useTranslations } from "next-intl";

import { FormButton } from "@/components/form/FormButton";
import { FormFieldError, FormFieldHint, FormFieldLabel } from "@/components/form/FormField";
import { FormInput } from "@/components/form/FormInput";
import { FormTextarea } from "@/components/form/FormTextarea";
import { useForm } from "@/components/form/hooks/useForm";
import { useFormField } from "@/components/form/hooks/useFormField";
import { useStore } from "@/store";
import { twMerge } from "@/utils/twMerge";

import { UploadTeamAvatarForm } from "../update-team-form/UploadTeamAvatarForm";

import { UserEmailsSection } from "./UserEmailsSection";

import type { UpdateUserFields } from "@/store/user/UpdateUserForm";
import type { ComponentPropsWithRef } from "react";

export const UpdateUserForm = observer(function UpdateUserForm() {
  const t = useTranslations();
  const id = useId();
  const { teams, dialog } = useStore();
  const { updateUserForm } = dialog.teamDialog;
  const { personalTeam } = teams;

  const { formProps, register, status, serverError, clearServerError } = useForm<UpdateUserFields>(
    (_fields, e) => {
      e.preventDefault();
      return updateUserForm.submit();
    },
  );
  const nameFormField = useFormField("name", updateUserForm, register, {
    onChange: clearServerError,
  });
  const userNameFormField = useFormField("username", updateUserForm, register, {
    onChange: clearServerError,
  });
  const aboutFormField = useFormField("about", updateUserForm, register, {
    onChange: clearServerError,
  });
  const disabled = !updateUserForm.isReady;

  return (
    <form className={twMerge("flex flex-col")} noValidate {...formProps}>
      {personalTeam && <UploadTeamAvatarForm team={personalTeam} className="mb-5 mt-1" />}
      <div className="flex flex-col mb-5">
        <FormFieldLabel htmlFor={`${id}-name`}>
          {t("auth.addFullName.fullNameInput.placeholder")}
        </FormFieldLabel>
        <FormInput
          id={`${id}-name`}
          placeholder={t("auth.addFullName.fullNameInput.placeholder")}
          keepPlaceholderOnTop={false}
          type="text"
          className="dark:bg-gray-800"
          disabled={disabled}
          {...nameFormField.inputProps}
        />
        <FormFieldError message={nameFormField.errorMessage} />
      </div>
      <div className="flex flex-col mb-5">
        <FormFieldLabel htmlFor={`${id}-username`}>
          {t("auth.addUsername.usernameInput.placeholder")}
        </FormFieldLabel>
        <FormInput
          id={`${id}-username`}
          placeholder={t("auth.addUsername.usernameInput.placeholder")}
          keepPlaceholderOnTop={false}
          type="text"
          valuePrefix="@"
          className="dark:bg-gray-800"
          disabled={disabled}
          {...userNameFormField.inputProps}
        />
        <FormFieldError message={userNameFormField.errorMessage} />
        <FormFieldHint message={t("auth.addUsername.usernameHint")} />
        <FormFieldError message={serverError} />
      </div>
      <UserEmailsSection className="mb-5" />
      <div className="flex flex-col mb-5">
        <FormFieldLabel htmlFor={`${id}-about`}>
          {t("createTeamDialog.stepAbout.placeholder")}
        </FormFieldLabel>
        <FormTextarea
          id={`${id}-about`}
          placeholder={t("createTeamDialog.stepAbout.placeholder")}
          keepPlaceholderOnTop={false}
          className="dark:bg-gray-800"
          disabled={disabled}
          {...(aboutFormField.inputProps as ComponentPropsWithRef<"textarea">)}
        />
        <FormFieldError message={aboutFormField.errorMessage} />
        <FormFieldHint message={t("teams.updateForm.bioHint")} />
        <FormFieldError message={serverError} />
      </div>
      <FormButton
        isBusy={status === "submitting"}
        disabled={disabled}
        variant="accent"
        size="sm"
        type="submit"
        className="w-full max-w-[150px] mr-auto sm:max-w-none"
      >
        {t("teams.updateForm.submitButton")}
      </FormButton>
    </form>
  );
});
